import { Befehl9 } from '../../../models'
import {
  BefehlAuftrag_9_40Dto,
  BefehlAuftrag_9_67Dto,
  BefehlAuftrag_9_68Dto,
  BefehlAuftrag_9_70Dto,
  BefehlAuftrag_9_75Dto
} from '../../../dtos'

import { Befehl9FormData, Befehl9FormSchema } from './schema'
import {
  Befehl9DataDto,
  Feld_9_43Dto,
  Feld_9_44Dto,
  Feld_9_47_1Dto,
  Feld_9_47_2Dto,
  Feld_9_48_1Dto,
  Feld_9_48_2Dto
} from '../../../dtos/befehl-9-dto'
import {
  createLocationField,
  createSimpleAuftrag,
  createZugmeldestelleField,
  findAuftragByKennung,
  hasAuftrag,
  isSelectorInOrZwischen
} from '../converter-utils'

export const convertModelToBefehl9FormData = (
  befehl?: Befehl9
): Befehl9FormData => {
  const formData = Befehl9FormSchema.parse({})

  if (!befehl?.auftraege) {
    return formData
  }

  // auftrag_9_40 enabled not changeable by user
  const auftrag940 = findAuftragByKennung(befehl.auftraege, '9.40')
  if (auftrag940) {
    // Extract zugmeldestelle fields
    const feld943 = auftrag940.felder?.find((f) => f.kennung === '9.43') as
      | Feld_9_43Dto
      | undefined
    if (feld943?.werte?.zugmeldestelle) {
      formData.auftrag_9_40.feld_9_43_zugmeldestelle = {
        art: feld943.werte.zugmeldestelle.art || '',
        name: feld943.werte.zugmeldestelle.name || ''
      }
    }

    const feld944 = auftrag940.felder?.find((f) => f.kennung === '9.44') as
      | Feld_9_44Dto
      | undefined
    if (feld944?.werte?.zugmeldestelle) {
      formData.auftrag_9_40.feld_9_44_zugmeldestelle = {
        art: feld944.werte.zugmeldestelle.art || '',
        name: feld944.werte.zugmeldestelle.name || ''
      }
    }

    // Extract location fields (9.47)
    extractLocationFields9_47(auftrag940 as BefehlAuftrag_9_40Dto, formData)

    // Extract location fields (9.48)
    extractLocationFields9_48(auftrag940 as BefehlAuftrag_9_40Dto, formData)

    // Determine selector based on which fields are present
    if (feld943 && feld944) {
      formData.auftrag_9_40.selector = 'zwischen'
    } else if (feld943) {
      formData.auftrag_9_40.selector = 'in'
    }
  }

  formData.auftrag_9_67.enabled = hasAuftrag(befehl.auftraege, '9.67')
  formData.auftrag_9_68.enabled = hasAuftrag(befehl.auftraege, '9.68')
  formData.auftrag_9_70.enabled = hasAuftrag(befehl.auftraege, '9.70')
  formData.auftrag_9_75.enabled = hasAuftrag(befehl.auftraege, '9.75')

  return formData
}

function extractLocationFields9_47(
  auftrag940: BefehlAuftrag_9_40Dto,
  formData: Befehl9FormData
) {
  const feld947_1 = auftrag940.felder?.find((f) => f.kennung === '9.47.1')
  const feld947_2 = auftrag940.felder?.find((f) => f.kennung === '9.47.2')
  if (feld947_1?.werte) {
    formData.auftrag_9_40.feld_9_47_type = 'km'
    formData.auftrag_9_40.feld_9_47_1_km = feld947_1.werte.km || ''
  } else if (feld947_2?.werte) {
    formData.auftrag_9_40.feld_9_47_type = 'signal'
    formData.auftrag_9_40.feld_9_47_2_signal = feld947_2.werte.signal || ''
  }
}

function extractLocationFields9_48(
  auftrag940: BefehlAuftrag_9_40Dto,
  formData: Befehl9FormData
) {
  const feld948_1 = auftrag940.felder?.find((f) => f.kennung === '9.48.1')
  const feld948_2 = auftrag940.felder?.find((f) => f.kennung === '9.48.2')
  if (feld948_1?.werte) {
    formData.auftrag_9_40.feld_9_48_type = 'km'
    formData.auftrag_9_40.feld_9_48_1_km = feld948_1.werte.km || ''
  } else if (feld948_2?.werte) {
    formData.auftrag_9_40.feld_9_48_type = 'signal'
    formData.auftrag_9_40.feld_9_48_2_signal = feld948_2.werte.signal || ''
  }
}

export const convertBefehl9FormDataToModel = (
  data: Befehl9FormData,
  frontendId: string
): Befehl9 => {
  const auftraege: Befehl9DataDto = []

  if (data?.auftrag_9_40?.enabled) {
    handleAuftrag9_40FormDataToModel(data, auftraege)
  }

  const auftrag967 = createSimpleAuftrag('9.67', data.auftrag_9_67?.enabled)
  if (auftrag967) {
    auftraege.push(auftrag967 as BefehlAuftrag_9_67Dto)
  }

  const auftrag968 = createSimpleAuftrag('9.68', data.auftrag_9_68?.enabled)
  if (auftrag968) {
    auftraege.push(auftrag968 as BefehlAuftrag_9_68Dto)
  }

  const auftrag970 = createSimpleAuftrag('9.70', data.auftrag_9_70?.enabled)
  if (auftrag970) {
    auftraege.push(auftrag970 as BefehlAuftrag_9_70Dto)
  }

  const auftrag975 = createSimpleAuftrag('9.75', data.auftrag_9_75?.enabled)
  if (auftrag975) {
    auftraege.push(auftrag975 as BefehlAuftrag_9_75Dto)
  }

  return {
    kennung: '9',
    frontendId,
    auftraege
  }
}

function handleAuftrag9_40FormDataToModel(
  data: Befehl9FormData,
  auftraege: Befehl9DataDto
) {
  const auftrag940: BefehlAuftrag_9_40Dto = {
    kennung: '9.40',
    felder: []
  }

  if (isSelectorInOrZwischen(data.auftrag_9_40?.selector)) {
    const feld943 = createZugmeldestelleField(
      '9.43',
      data.auftrag_9_40?.feld_9_43_zugmeldestelle?.art,
      data.auftrag_9_40?.feld_9_43_zugmeldestelle?.name
    )
    if (feld943) {
      auftrag940.felder.push(feld943 as Feld_9_43Dto)
    }

    if (data.auftrag_9_40?.selector === 'zwischen') {
      const feld944 = createZugmeldestelleField(
        '9.44',
        data.auftrag_9_40?.feld_9_44_zugmeldestelle?.art,
        data.auftrag_9_40?.feld_9_44_zugmeldestelle?.name
      )
      if (feld944) {
        auftrag940.felder.push(feld944 as Feld_9_44Dto)
      }
    }
  }
  const feld947 = createLocationField(
    '9.47',
    data.auftrag_9_40?.feld_9_47_type,
    data.auftrag_9_40?.feld_9_47_1_km,
    data.auftrag_9_40?.feld_9_47_2_signal
  )
  if (feld947) {
    auftrag940.felder.push(feld947 as Feld_9_47_1Dto | Feld_9_47_2Dto)
  }

  const feld948 = createLocationField(
    '9.48',
    data.auftrag_9_40?.feld_9_48_type,
    data.auftrag_9_40?.feld_9_48_1_km,
    data.auftrag_9_40?.feld_9_48_2_signal
  )
  if (feld948) {
    auftrag940.felder.push(feld948 as Feld_9_48_1Dto | Feld_9_48_2Dto)
  }

  auftraege.push(auftrag940)
}
